import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArticleFormContent } from './components/ArticleForm';
import { ArticleMetadata } from './components/ArticleMetadata';
import { ArticleSheetHeader } from './components/ArticleSheetHeader';
import { UnsavedChangesDialog } from './components/UnsavedChangesDialog';
import { useArticleSheet } from './hooks/useArticleSheet.hook';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { Form } from '@/Domain/components/Shared/Form/Form';
import { Sheet, SheetContent } from '@/Domain/components/Shared/Sheet/sheet';
import { useSocket } from '@/Domain/components/SocketProvider';
import { ArticleStateChange, ArticleStateEnum } from '@/Domain/components/TopBar/types';
import { useAutoRefresh } from '@/Domain/hooks/KnowledgeBase/useAutoRefresh.hook';
import { useRefreshArticle } from '@/Domain/hooks/KnowledgeBase/useRefreshArticle.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { editPausesAutoRefresh } from '@/Domain/utils/article';

export const ArticleSheet = () => {
  const { t } = useTranslation(['knowledge_base']);
  const { isOpen, toggleModal, closeModal } = useModalStore();
  const navigate = useNavigate();
  const { scenario, id: articleIdFromParams } = useParams<{ scenario?: string; id?: string }>();
  const { form, article, isLoading, createSubmitHandler } = useArticleSheet();
  const { refreshArticle, isPending: isMutationPending } = useRefreshArticle();
  const { settings: autoRefreshSettings } = useAutoRefresh();
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  const suppressCloseRef = useRef(false);
  const hasLocalChangesRef = useRef(false);
  const refreshTriggeredFromSheetRef = useRef(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isRefreshInProgress, setIsRefreshInProgress] = useState(false);

  // Combined refresh state: either mutation is pending OR background refresh is in progress
  const isRefreshing = isMutationPending || isRefreshInProgress;

  // Listen to socket events for refresh state changes
  useEffect(() => {
    // Use articleIdFromParams for consistency with query key (which uses params id)
    if (!articleIdFromParams) return;

    const handleArticleStateChanged = (data: ArticleStateChange) => {
      if (String(data.article_id) !== articleIdFromParams) {
        return;
      }

      // Set refresh in progress when receiving REFRESHING state
      if (data.state === ArticleStateEnum.REFRESHING) {
        setIsRefreshInProgress(true);
        return;
      }

      // Clear refresh state and show results on completion
      if (
        data.state === ArticleStateEnum.DONE ||
        data.state === ArticleStateEnum.ERROR ||
        data.state === ArticleStateEnum.REFRESH_FAILED
      ) {
        const wasTriggeredFromSheet = refreshTriggeredFromSheetRef.current;
        setIsRefreshInProgress(false);
        refreshTriggeredFromSheetRef.current = false;

        // Invalidate article query using params id to ensure query key match
        queryClient.invalidateQueries({
          queryKey: [QueryKey.knowledgeBase.article, articleIdFromParams],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.knowledgeBase.articlesInfinite],
        });

        // Only show toast if refresh was triggered from this sheet (avoid duplicate with KnowledgeManager)
        if (wasTriggeredFromSheet) {
          if (data.state === ArticleStateEnum.DONE) {
            toast.success(t('knowledge_base:article.toast.refresh_completed'));
          } else {
            toast.error(t('knowledge_base:article.toast.refresh_failed'));
          }
        }
      }
    };

    socket.on(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);

    return () => {
      socket.off(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);
    };
  }, [articleIdFromParams, socket, queryClient, t]);

  const SHEET_ANIMATION_DURATION = 320;

  const navigateToKnowledgeBase = useCallback(() => {
    const path = scenario
      ? `/i/${scenario}/${IndexPath.knowledgeBase.root}`
      : `/i/${IndexPath.knowledgeBase.root}`;
    navigate(path);
  }, [navigate, scenario]);

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.ARTICLE_SHEET)) {
      toggleModal(MODAL_NAMES.ARTICLE_SHEET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track local changes once dirty
  const {
    formState: { isDirty: isFormDirty },
  } = form;
  useEffect(() => {
    if (isFormDirty) {
      hasLocalChangesRef.current = true;
    } else {
      // When the form is reset back to clean state (e.g., after save), clear the flag
      hasLocalChangesRef.current = false;
    }
  }, [isFormDirty]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmationModalOpen) return;

        const target = e.target as HTMLElement | null;
        if (target && target.closest('#article_tags')) {
          e.preventDefault();
          e.stopPropagation();
          (document.activeElement as HTMLElement | null)?.blur();
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        handleSheetOpenChange(false);
      }
    },
    [confirmationModalOpen],
  );

  // Global ESC key handling
  useEffect(() => {
    if (!isOpen(MODAL_NAMES.ARTICLE_SHEET)) return;

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen(MODAL_NAMES.ARTICLE_SHEET), handleKeyDown]);

  // Sheet open/close handler
  const handleSheetOpenChange = useCallback(
    (open: boolean) => {
      // Ignore attempts to close the sheet while confirmation dialog is active
      if (suppressCloseRef.current && !open) return;
      if (!open) {
        if (isFormDirty || hasLocalChangesRef.current) {
          suppressCloseRef.current = true;
          setConfirmationModalOpen(true);
          return;
        }

        closeModal();
        setTimeout(() => {
          navigateToKnowledgeBase();
        }, SHEET_ANIMATION_DURATION);
      }
    },
    [isFormDirty, closeModal, navigateToKnowledgeBase],
  );

  // Confirmation dialog handler
  const handleConfirmationOpenChange = useCallback(
    (open: boolean) => {
      setConfirmationModalOpen(open);
      if (!open) {
        // Defer clearing suppression so the same ESC event doesn't also close the Sheet
        setTimeout(() => {
          suppressCloseRef.current = false;
        }, 0);
      } else {
        suppressCloseRef.current = true;
      }
    },
    [setConfirmationModalOpen],
  );

  // Form change handler
  const handleFormChange = useCallback(() => {
    // This will be handled by the custom hook
  }, []);

  // Form submit handler
  const handleSave = useCallback(() => {
    // Saying it at the moment of action: a real content/title edit to a URL
    // article opts it out of the account's auto-refresh until re-synced.
    const pausesAutoRefresh = editPausesAutoRefresh(
      article.data,
      form.formState.dirtyFields,
      autoRefreshSettings,
    );
    const submit = createSubmitHandler({
      onSuccess: () => {
        if (pausesAutoRefresh) {
          toast.success(t('knowledge_base:article.toast.saved_auto_refresh_paused'), {
            duration: 8000,
          });
        } else {
          toast.success(t('knowledge_base:article.toast.update_successfully'));
        }
        closeModal();
        setTimeout(() => {
          navigateToKnowledgeBase();
        }, SHEET_ANIMATION_DURATION);
      },
    });
    form.handleSubmit(submit)();
  }, [
    createSubmitHandler,
    form,
    article.data,
    autoRefreshSettings,
    closeModal,
    navigateToKnowledgeBase,
    t,
  ]);

  // Save hotkey: Cmd+S / Ctrl+S
  useHotkeys(
    ['meta+s', 'ctrl+s'],
    (event) => {
      // Only when the Article sheet is open
      if (!isOpen(MODAL_NAMES.ARTICLE_SHEET)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!confirmationModalOpen && !isLoading) {
        handleSave();
      }
    },
    [isOpen, confirmationModalOpen, isLoading, handleSave],
    { enableOnFormTags: true, enableOnContentEditable: true },
  );

  // Confirmation dialog confirm handler
  const handleConfirmClose = useCallback(() => {
    setConfirmationModalOpen(false);
    closeModal();
    form.reset();
    hasLocalChangesRef.current = false;
    setTimeout(() => {
      navigateToKnowledgeBase();
    }, SHEET_ANIMATION_DURATION);
  }, [closeModal, form, navigateToKnowledgeBase]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (articleIdFromParams) {
      refreshTriggeredFromSheetRef.current = true;
      setIsRefreshInProgress(true);
      refreshArticle(Number(articleIdFromParams));
    }
  }, [articleIdFromParams, refreshArticle]);

  // Form keydown handler
  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If ESC is pressed inside Tags, only blur the focused input.
        const target = e.target as HTMLElement | null;
        if (target && target.closest('#article_tags')) {
          e.preventDefault();
          e.stopPropagation();
          (document.activeElement as HTMLElement | null)?.blur();
          return;
        }

        // Mirror the X button behavior instead of letting Radix close the Sheet directly
        e.preventDefault();
        e.stopPropagation();
        if (!confirmationModalOpen) handleSheetOpenChange(false);
      }
    },
    [confirmationModalOpen, handleSheetOpenChange],
  );

  return (
    <Form {...form}>
      <Sheet open={isOpen(MODAL_NAMES.ARTICLE_SHEET)} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          className="!max-w-[100vw] xl:!max-w-[80%] 2xl:!max-w-6xl w-full"
          tabIndex={undefined}
          asChild
        >
          <form className="h-full" onChange={handleFormChange} onKeyDownCapture={handleFormKeyDown}>
            <ArticleSheetHeader
              onSave={handleSave}
              onClose={() => handleSheetOpenChange(false)}
              isLoading={isLoading}
            />

            <div className="mt-0 h-[calc(100%-64px)] overflow-y-auto border-t border-border lg:overflow-hidden">
              <div className="grid h-full min-h-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.4fr)]">
                <ArticleFormContent form={form} />
                <div className="custom-scroll h-full min-h-0 min-w-0 pl-1 lg:overflow-y-auto lg:border-l lg:border-border">
                  <ArticleMetadata
                    article={article.data}
                    form={form}
                    onRefresh={handleRefresh}
                    isRefreshing={isRefreshing}
                  />
                </div>
              </div>
            </div>

            <UnsavedChangesDialog
              open={confirmationModalOpen}
              onOpenChange={handleConfirmationOpenChange}
              onConfirmClose={handleConfirmClose}
            />
          </form>
        </SheetContent>
      </Sheet>
    </Form>
  );
};
